/* --------------------------------------------------------------------------
    PREMIUM AGENCY PORTFOLIO (SEO OPTIMIZED)
    FIX: Instant Loading on Route Change (Removed Scroll Triggers)
    FIX: LegionX Carousel Swipeable on Mobile (Added drag gestures)
    FIX: Added Visible Navigation Arrows on Mobile & Desktop
    UPDATE: Rounded Cards
--------------------------------------------------------------------------- */

import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet"; // IMPORTED FOR SEO
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowUpRight, 
  Play, 
  X, 
  ChevronLeft, 
  ChevronRight 
} from "lucide-react";

/* --------------------------------------------------------------------------
    THEME CONSTANTS
--------------------------------------------------------------------------- */
const THEME = {
  bg: "bg-[#F3F2ED]",
  textMain: "text-[#0F0F0F]",
  textMuted: "text-[#6B6A65]",
  border: "border-[#0F0F0F]/10",
  accent: "bg-[#0F0F0F]",
};

const CATEGORY_ORDER = ["Explainers", "Branding", "Product Demos"];

/* --------------------------------------------------------------------------
    ANIMATION VARIANTS (Simple & Modern)
--------------------------------------------------------------------------- */
const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1, 
      delayChildren: 0.1
    }
  },
  exit: { opacity: 0 }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.4, 
      ease: [0.25, 1, 0.5, 1] 
    }
  },
  exit: { opacity: 0, y: -10, transition: { duration: 0.2 } }
};

/* --------------------------------------------------------------------------
    UI COMPONENTS
--------------------------------------------------------------------------- */

const CategoryFilter = ({ active, label, onClick }) => (
  <button
    onClick={onClick}
    aria-pressed={active}
    className="relative px-1 py-2 group"
  >
    <span className={`text-xs md:text-sm tracking-widest uppercase font-bold transition-colors duration-300 ${
      active ? THEME.textMain : "text-black/40 group-hover:text-black/70"
    }`}>
      {label}
    </span>
    {active && (
      <motion.div
        layoutId="activeUnderline"
        className="absolute bottom-0 left-0 right-0 h-[2px] bg-black"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.3 }}
      />
    )}
  </button>
);

/* --------------------------------------------------------------------------
    CAROUSEL COMPONENT
--------------------------------------------------------------------------- */
const ThumbnailCarousel = ({ images, title, onClick }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const paginate = (newDirection) => {
    setCurrentIndex((prev) => (prev + newDirection + images.length) % images.length);
  };

  return (
    <div className="relative w-full h-full overflow-hidden group rounded-2xl bg-[#E5E4DE]">
      <div onClick={onClick} className="w-full h-full cursor-pointer">
        <AnimatePresence initial={false} mode="popLayout">
          <motion.img
            key={currentIndex}
            src={images[currentIndex]}
            alt={`${title} view ${currentIndex + 1}`} // SEO: Descriptive Alt
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            loading="lazy" // SEO: Lazy load for speed
            className="absolute inset-0 w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 transition-all duration-700"
          />
        </AnimatePresence>
      </div>

      <div className="absolute bottom-4 right-4 flex gap-2 z-20 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <button 
            aria-label="Previous image"
            onClick={(e) => { e.stopPropagation(); paginate(-1); }} 
            className="p-2 bg-white/90 backdrop-blur-md rounded-full hover:bg-black hover:text-white transition-colors"
        >
          <ChevronLeft size={16} />
        </button>
        <button 
            aria-label="Next image"
            onClick={(e) => { e.stopPropagation(); paginate(1); }} 
            className="p-2 bg-white/90 backdrop-blur-md rounded-full hover:bg-black hover:text-white transition-colors"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

/* --------------------------------------------------------------------------
    MODAL CAROUSEL (FIXED: Visible Arrows + Swipe)
--------------------------------------------------------------------------- */
const ModalCarousel = ({ images, title }) => {
  const [index, setIndex] = useState(0);
  const paginate = useCallback(
    (newDirection) => setIndex((prev) => (prev + newDirection + images.length) % images.length),
    [images.length]
  );

  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset, velocity) => {
    return Math.abs(offset) * velocity;
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "ArrowRight") paginate(1);
      if (e.key === "ArrowLeft") paginate(-1);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [images.length, paginate]);

  return (
    <div className="relative w-full h-full flex items-center justify-center bg-[#F3F2ED] rounded-lg overflow-hidden group">
      <AnimatePresence initial={false} mode="wait">
        <motion.img
          key={index}
          src={images[index]}
          alt={`${title} detail view ${index + 1}`} // SEO: Descriptive Alt
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0, zIndex: 1 }}
          exit={{ opacity: 0, x: -20, zIndex: 0 }}
          transition={{ duration: 0.3 }}
          drag="x"
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={1}
          onDragEnd={(e, { offset, velocity }) => {
            const swipe = swipePower(offset.x, velocity.x);
            if (swipe < -swipeConfidenceThreshold) {
              paginate(1);
            } else if (swipe > swipeConfidenceThreshold) {
              paginate(-1);
            }
          }}
          className="w-full h-full object-contain max-h-[80vh] cursor-grab active:cursor-grabbing p-4 touch-pan-y"
        />
      </AnimatePresence>

      <button 
        aria-label="Previous image"
        onClick={(e) => { e.stopPropagation(); paginate(-1); }} 
        className="absolute left-2 md:left-4 p-3 md:p-4 rounded-full bg-white/80 hover:bg-white hover:scale-110 shadow-xl z-20 transition-all active:scale-95"
      >
        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-black" />
      </button>

      <button 
        aria-label="Next image"
        onClick={(e) => { e.stopPropagation(); paginate(1); }} 
        className="absolute right-2 md:right-4 p-3 md:p-4 rounded-full bg-white/80 hover:bg-white hover:scale-110 shadow-xl z-20 transition-all active:scale-95"
      >
        <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-black" />
      </button>
      
      <div className="absolute bottom-6 bg-black/5 backdrop-blur-md px-4 py-2 rounded-full text-xs font-mono tracking-widest z-10">
        {index + 1} / {images.length}
      </div>
    </div>
  );
};

/* --------------------------------------------------------------------------
    PROJECT ROW ITEM
--------------------------------------------------------------------------- */
const ProjectRow = ({ item, index, onClick }) => {
  const isCarousel = item.images && item.images.length > 1;
  const isVideo = item.videoUrl && item.videoUrl.startsWith("http");

  return (
    <motion.div
      variants={itemVariants}
      className="group mb-24 last:mb-0"
      // SEO: Mark this as a portfolio piece section
      role="article"
      aria-label={`${item.title} Project`}
    >
      <div className="flex flex-col md:flex-row gap-8 md:gap-16 items-start">
        
        {/* 1. Typography Side */}
        <div className="w-full md:w-1/3 md:sticky md:top-32 pt-4">
          <div className="flex items-center gap-3 mb-6 opacity-40">
              <span className="text-sm font-mono">{(index + 1).toString().padStart(2, '0')}</span>
              <div className="h-[1px] w-8 bg-black"></div>
              <span className="text-xs font-bold uppercase tracking-widest">{item.category}</span>
          </div>

          <h3 
            onClick={() => onClick(item)}
            className="text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95] mb-6 cursor-pointer hover:opacity-60 transition-opacity"
          >
            {item.title}
          </h3>

          <p className={`${THEME.textMuted} text-lg font-light leading-relaxed mb-8 max-w-sm`}>
            {item.longDescription}
          </p>

          <button 
            onClick={() => onClick(item)}
            className="group/btn flex items-center gap-2 text-sm uppercase font-bold tracking-widest border-b border-black pb-1 hover:text-black/60 hover:border-black/60 transition-all"
          >
            See work
            <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
          </button>
        </div>

        {/* 2. Visual Side */}
        <div className="w-full md:w-2/3">
          <div className="relative overflow-hidden aspect-[16/10] bg-[#E5E4DE] shadow-sm rounded-2xl">
            {isCarousel ? (
              <ThumbnailCarousel images={item.images} title={item.title} onClick={() => onClick(item)} />
            ) : (
              <div 
                className="w-full h-full cursor-pointer overflow-hidden relative" 
                onClick={() => onClick(item)}
              >
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors z-10 duration-500" />
                
                <motion.img
                  whileHover={{ scale: 1.03 }}
                  transition={{ duration: 0.7 }}
                  src={item.thumbnail}
                  alt={`${item.title} cover image`} // SEO: Descriptive Alt
                  loading="lazy" // SEO: Lazy load
                  className="w-full h-full object-cover grayscale-[10%] group-hover:grayscale-0 transition-all duration-700 scale-[1.15]" 
                />
                
                {isVideo && (
                  <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
                    <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/40 group-hover:scale-110 transition-transform duration-500">
                      <Play className="w-8 h-8 fill-white text-white ml-1" />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>
      
      {/* Editorial Divider */}
      <div className="w-full h-[1px] bg-black/10 mt-24" />
    </motion.div>
  );
};

/* --------------------------------------------------------------------------
    MAIN COMPONENT
--------------------------------------------------------------------------- */
const OurWorks = () => {
  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedItem, setSelectedItem] = useState(null);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const getYoutubeThumbnailUrl = (url) => {
    if (!url) return "https://placehold.co/800x450/E5E4DE/9CA3AF?text=No+Preview";
    let videoId = "";
    if (url.includes("v=")) videoId = url.split("v=")[1].split("&")[0];
    else if (url.includes("youtu.be/")) videoId = url.split("youtu.be/")[1].split("?")[0];
    return videoId ? `https://img.youtube.com/vi/${videoId}/sddefault.jpg` : "https://placehold.co/800x450/E5E4DE/9CA3AF";
  };

  const allProjects = useMemo(() => [
    {
      category: "Explainers",
      title: "Dhatri Financial Services",
      longDescription: "Transforming complex financial data into an engaging visual narrative. Clean geometric shapes made investment strategies accessible and contributed to a notable boost in the client’s business.",
      videoUrl: "https://www.youtube.com/watch?v=xA7DG9z2uj4",
      thumbnail: getYoutubeThumbnailUrl("https://www.youtube.com/watch?v=xA7DG9z2uj4"),
    },
    {
      category: "Explainers",
      title: "Ryich Delicacies",
      longDescription: "A sensory-driven motion piece focused on logo reveal animation.",
      videoUrl: "https://www.youtube.com/watch?v=Y5hqc42F1X4",
      thumbnail: getYoutubeThumbnailUrl("https://www.youtube.com/watch?v=Y5hqc42F1X4"),
    },
    {
      category: "Branding",
      title: "Framelync Kinetic Identity",
      longDescription: "Bold exploration of typography in motion. This internal project showcases our ability to create rhythm using nothing but text and timing.",
      videoUrl: "https://www.youtube.com/watch?v=x4El_DkAXPo",
      thumbnail: getYoutubeThumbnailUrl("https://www.youtube.com/watch?v=x4El_DkAXPo"),
    },
    {
      category: "Branding",
      title: "LegionX Gaming Cafe",
      longDescription: "Our design for a cafe based in Mysore, Karnataka. A complete end-to-end branding project.",
      images: [
         "legionx-logo-dimensions.png",
         "legionx-entrance.png",
         "legionx-mainboard.png", 
         "legionx-outsideadposter.png",
      ],
      thumbnail: "legionx-logo-dimensions.png",
      mediaType: "image",
      isImage: true,
    },
    {
      category: "Product Demos",
      title: "Spice Packaging 3D Model",
      longDescription: "Photorealistic 3D rendering. The launch created strong traction across the client's social media channels.",
      mediaType: "image",
      thumbnail: "garam-masala-packaging.png", 
      isImage: true
    },
  ], []);

  const processedProjects = useMemo(() => {
    let filtered = activeCategory === "All" ? allProjects : allProjects.filter(p => p.category === activeCategory);
    return filtered.sort((a, b) => CATEGORY_ORDER.indexOf(a.category) - CATEGORY_ORDER.indexOf(b.category));
  }, [activeCategory, allProjects]);

  const handleOpen = (item) => {
    if (item.videoUrl && item.videoUrl.startsWith("http")) window.open(item.videoUrl, "_blank");
    else setSelectedItem(item);
  };

  // SEO: Schema Markup Generation
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Selected Works | Portfolio",
    "description": "Browse our portfolio of explainer videos, branding, and 3D product demos.",
    "hasPart": allProjects.map(project => ({
      "@type": "CreativeWork",
      "name": project.title,
      "description": project.longDescription,
      "genre": project.category,
      "thumbnailUrl": project.thumbnail,
      "url": project.videoUrl ? project.videoUrl : `https://www.framelync.com/our-works`
    }))
  };

  return (
    <>
    {/* SEO HEAD INJECTION */}
    <Helmet>
        <title>Selected Works | Portfolio | Framelync</title>
        <meta name="description" content="Explore our portfolio of selected works including explainer videos, branding projects, and product demos. We build digital products that blend strategy with motion." />
        <link rel="canonical" href="https://www.framelync.com/our-works" />
        
        {/* Open Graph / Social */}
        <meta property="og:title" content="Selected Works | Framelync Portfolio" />
        <meta property="og:description" content="We build digital products and experiences that blend strategy with motion." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.framelync.com/our-works" />
        
        {/* Schema Markup for Google */}
        <script type="application/ld+json">
          {JSON.stringify(schemaMarkup)}
        </script>
    </Helmet>

    {/* CHANGED: div -> main (Semantic HTML for SEO) */}
    <main className={`min-h-screen ${THEME.bg} ${THEME.textMain} font-sans selection:bg-black selection:text-white`}>
      
      {/* --- HEADER --- */}
      <div className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-[90rem] mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-12 border-b border-black/10 pb-12">
            <div>
              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: "easeOut" }}
                className="text-6xl md:text-8xl font-semibold tracking-tighter mb-6"
              >
                Selected Works<span className="text-black/20">.</span>
              </motion.h1>
              <p className="text-xl text-[#6B6A65] font-light max-w-xl">
                We build digital products and experiences that blend strategy with motion.
              </p>
            </div>

            <nav aria-label="Portfolio Category Filter" className="flex gap-4">
              <CategoryFilter active={activeCategory === "All"} label="All" onClick={() => setActiveCategory("All")} />
              {CATEGORY_ORDER.map(cat => (
                <CategoryFilter key={cat} active={activeCategory === cat} label={cat} onClick={() => setActiveCategory(cat)} />
              ))}
            </nav>
          </div>
        </div>
      </div>

      {/* --- PROJECTS STREAM (Fixed: Stagger Container) --- */}
      <motion.div 
        className="max-w-[90rem] mx-auto px-6 md:px-12 lg:px-24 pb-20"
        variants={containerVariants}
        initial="hidden"
        animate="show"
      >
        <AnimatePresence mode="wait">
           {/* Note: 'key' on the parent div helps AnimatePresence detect changes */}
          <motion.div 
            key={activeCategory} 
            variants={containerVariants}
            initial="hidden"
            animate="show"
            exit="exit"
          >
            {processedProjects.map((project, index) => (
              <ProjectRow 
                key={project.title} 
                item={project} 
                index={index} 
                onClick={handleOpen}
              />
            ))}
          </motion.div>
        </AnimatePresence>
      </motion.div>

       {/* --- MODAL --- */}
       <AnimatePresence>
        {selectedItem && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedItem(null)}
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#F3F2ED]/95 backdrop-blur-xl p-4 sm:p-8"
          >
            <motion.div 
              layoutId={`img-${selectedItem.title}`}
              className="relative w-full max-w-7xl max-h-[90vh] flex flex-col shadow-2xl rounded-2xl overflow-hidden bg-white"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center p-6 border-b border-black/5 bg-white z-10">
                <h3 className="text-xl font-bold tracking-tight">{selectedItem.title}</h3>
                <button 
                  onClick={() => setSelectedItem(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                  aria-label="Close modal"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              
              <div className="relative flex-1 bg-[#F3F2ED] overflow-hidden flex items-center justify-center">
                {selectedItem.images && selectedItem.images.length > 1 ? (
                  <ModalCarousel images={selectedItem.images} title={selectedItem.title} />
                ) : (
                  <img
                    src={selectedItem.thumbnail}
                    alt={selectedItem.title}
                    className="w-full h-full object-contain p-4 sm:p-8"
                  />
                )}
              </div>
              
              <div className="p-6 bg-white border-t border-black/5 md:hidden">
                  <p className="text-sm text-gray-500">{selectedItem.longDescription}</p>
              </div>

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </main>
    </>
  );
};

export default OurWorks;